<template lang="pug">
.row(v-if="!loading")
  .form-title
    .form-title-text {{ title }}
    .form-title-close(@click="requestClose({})")
      inline-svg.form-card-close-icon(:src="require('../../../../../assets/icons/form/header/close.svg')")
    .form-title-spacer
  step-one(
    :name="name",
    :facility="facility",
    :contract="contract",
    @update-name="updateNameField",
    @update-facility="updateFacility",
    @update-contract="onContractChange"
  )
  step-two(
    :readonly="facilityBlank",
    :buildings="getInitialValues(building)",
    :watch="getDynamicWatch('facility', facility.id)",
    :selectParams="{ facility_id: facility.id }",
    @update-building="updateBuilding"
  )
  step-three(
    :groupSystems="groupSystemIds",
    :systems="getInitialValues(system)",
    :equipments="getInitialValues(equipment)",
    :groupSystemReadonly="facilityBlank",
    :systemReadonly="groupSystemBlank",
    :equipmentReadonly="systemBlank",
    :groupSystemWatch="getDynamicWatch('facility', facility.id)",
    :systemWatch="getDynamicWatch('ppr_group_system_id', groupSystemIds)",
    :equipmentWatch="[ { name: 'building_id', value: getBuildingParams() }, { name: 'ppr_system_id', value: getSystemParams() }, ]",
    :groupSystemParams="{ facility_id: facility.id }",
    :systemParams="{ ppr_group_system_id: groupSystemIds }",
    :equipmentParams="{ ppr_system_id: getSystemParams(), building_id: getBuildingParams() }",
    :radioType="type",
    @update-group-system="updateGroupSystem",
    @update-system="updateSystem",
    @update-equipment="updateEquipment",
    @update-radio="updateType"
  )
  step-four(
    :events="events",
    :facilityId="facility.id",
    @close-event="closeEvent",
    @add-event="addEvent",
    @remove-event="removeEvent"
  )
  .form-submit-wrapper
    q-btn.form-submit(unelevated, no-caps, @click="submitForm()", :disable="disabledButton") {{ schedulerLocales["submit"] }}

  q-dialog(v-model="confirmModalShown")
    confirm-modal(@confirm="handleCloseConfirmation", :parentData="{ message: confirmCloseMessage }")
  q-dialog(v-model="deleteEventConfirmModalShown")
    confirm-modal(
      @confirm="handleDeletedEventsConfirmation",
      :parentData="{ message: deletedEventsConfirmMessage, subtext: deletedEventsConfirmSubtext, confirmLabel: yesLabel, cancelLabel: noLabel }"
    )
.row(v-else)
  .spinner-container.form-spinner
    q-spinner(color="primary", size="3em")
</template>

<script setup>
import { computed, ref, onBeforeMount, onMounted, onBeforeUnmount, provide } from "vue";
import { cloneDeep, isEqual } from "lodash";
import selectField from "../../../../shared/general_components/fields/selectField";
import dateField from "../../../../shared/general_components/fields/dateField";
import confirmModal from "../../../../shared/helpers/confirmModal.vue";
import backend from "@/api";
import i18n from "@/plugins/vue-i18n";
import { handleError } from "@/services/handleErrors";
import { useStore } from "@/store";
import { Notify } from "quasar";
import { getDynamicWatch } from "@/services/useWatchParent";
import { currentLocale, pprScheduleLocales, notifies } from "@/services/useLocales";
import { useEmitter } from "@/services/useEmitter";
import { parse } from "date-fns";
import stepOne from "./stepOne";
import stepTwo from "./stepTwo";
import stepThree from "./stepThree";
import stepFour from "./stepFour";

const props = defineProps({
  method: { type: String, default: () => "create" },
  title: { type: String, default: () => i18n["messages"][currentLocale.value]["scheduler"]["titleNew"] },
  id: { type: Number, default: () => null },
  copySourceId: { type: Number, default: () => null },
});
const emit = defineEmits(["close-form"]);
const store = useStore();
const emitter = useEmitter();

const name = ref("");
const facility = ref({});
const contract = ref({});
const building = ref([]);
const buildingsToRemove = ref([]);
const groupSystems = ref([]);
const system = ref([]);
const systemToRemove = ref([]);
const type = ref("building");
const equipment = ref([]);
const equipmentToRemove = ref([]);
const events = ref([]);
const eventsToRemove = ref([]);
const removedEvents = ref([]);
const loading = ref(true);
const formSubmiting = ref(false);
const disabledButton = ref(false);
const confirmModalShown = ref(false);
const deleteEventConfirmModalShown = ref(false);
const pendingCloseData = ref(null);
const initialFormSnapshot = ref(null);
const gridName = "ppr_scheduler";
let nextNewEventKey = 0;

const systemBlank = computed(() => !system.value.length > 0);
const facilityBlank = computed(() => !facility.value.id);
const groupSystemIds = computed(() => groupSystems.value.map(item => item.id));
const groupSystemBlank = computed(() => groupSystems.value.length === 0);
const schedulerLocales = computed(() => i18n["messages"][currentLocale.value]["scheduler"]);
const dateLocales = computed(() => i18n["messages"][currentLocale.value]["date"]);
const confirmCloseMessage = computed(() => notifies.value.confirm_close_form);
const deletedEventNames = computed(() => removedEvents.value.map(event => `"${event.name}"`).join(", "));
const deletedEventsConfirmMessage = computed(() => schedulerLocales.value.deletedEventsConfirm);

const deletedEventsConfirmSubtext = computed(() => {
  const localeKey = removedEvents.value.length === 1 ? "deletedEventConfirmSubtext" : "deletedEventsConfirmSubtext";
  return schedulerLocales.value[localeKey].replace(":events", deletedEventNames.value);
});

const yesLabel = computed(() => i18n["messages"][currentLocale.value].yes);
const noLabel = computed(() => i18n["messages"][currentLocale.value].no);
const isCloseConfirmationEnabled = computed(() =>
  Boolean(store.state.account.current_user?.confirm_filled_form_closing),
);

const isDirty = computed(() => {
  if (initialFormSnapshot.value === null) {
    return false;
  }

  return !isEqual(getComparableFormState(), initialFormSnapshot.value);
});

const periodTypeOptions = computed(() => [
  {
    id: "daily",
    title: pprScheduleLocales.value["form.daily"],
  },
  {
    id: "monthly_date",
    title: pprScheduleLocales.value["form.monthly_date"],
  },
  {
    id: "weekly",
    title: pprScheduleLocales.value["form.weekly"],
  },
  {
    id: "monthly",
    title: pprScheduleLocales.value["form.monthly"],
  },
  {
    id: "yearly",
    title: pprScheduleLocales.value["form.yearly"],
  },
]);
const path = computed(() => store.state.paths["ppr_schedule"]);

const closeForm = data => {
  emit("close-form", data);
};

const requestClose = (data = {}) => {
  if (isCloseConfirmationEnabled.value && isDirty.value) {
    pendingCloseData.value = data;
    confirmModalShown.value = true;
    return;
  }

  closeForm(data);
};

const handleExternalCloseRequest = request => {
  request?.markHandled?.();
  requestClose(request?.params || {});
};

const handleCloseConfirmation = val => {
  const data = pendingCloseData.value || {};

  confirmModalShown.value = false;
  pendingCloseData.value = null;

  if (val) {
    closeForm(data);
  }
};

const getComparableFormState = () => {
  return {
    name: name.value,
    facilityId: facility.value?.id || null,
    contractId: contract.value?.id || null,
    buildingIds: building.value.map(item => item.id),
    groupSystemIds: groupSystemIds.value,
    systemIds: system.value.map(item => item.id),
    type: type.value,
    equipmentIds: equipment.value.map(item => item.id),
    events: events.value.map(event => ({
      id: event.id || null,
      name: event.name,
      description: event.description,
      periodTypeId: event.periodType?.id || null,
      period: (event.period || []).filter(period => period.selected).map(period => period.id),
      startedAt: event.startedAt,
      finishedAt: event.finishedAt,
      deadline: event.deadline,
      checklistId: event.checklistId,
      checklistRequired: event.checklistRequired,
      works: event.works?.items?.map(work => ({
        id: work.id,
        count: work.count,
      })),
      materials: event.materials?.map(material => ({
        id: material.id,
        count: material.count,
      })),
    })),
  };
};

const captureInitialFormSnapshot = () => {
  initialFormSnapshot.value = cloneDeep(getComparableFormState());
};

const getInitialValues = values => {
  const arr = [];

  values.forEach(item => {
    arr.push(item.id);
  });

  return arr;
};

const updateFacility = async val => {
  if (!val?.value) {
    facility.value = {};
    contract.value = {};
    building.value = [];
    groupSystems.value = [];
    system.value = [];
    equipment.value = [];
    events.value = [];
    return;
  }

  facility.value = {
    id: val.value,
    title: val.title,
  };
  contract.value = {};
  building.value = [];
  groupSystems.value = [];
  system.value = [];
  equipment.value = [];
  events.value = [];
};

const onContractChange = async val => {
  if (!val?.value) {
    const hadContract = Boolean(contract.value?.id);
    contract.value = {};

    if (hadContract) {
      await reloadEventsWorks();
    }

    return;
  }

  if (val.value === contract.value?.id) {
    return;
  }

  contract.value = {
    id: val.value,
    title: val.title,
  };

  await reloadEventsWorks();
};

const updateBuilding = val => {
  building.value = [];

  val.forEach(item => {
    building.value.push({
      id: item.value,
      title: item.title,
    });
  });
};

const updateGroupSystem = val => {
  groupSystems.value = val.map(item => ({
    id: item.value,
    title: item.title,
  }));
};

const updateSystem = val => {
  system.value = [];

  val.forEach(item => {
    system.value.push({
      id: item.value,
      title: item.title,
    });
  });
};

const updateEquipment = val => {
  equipment.value = [];

  val.forEach(item => {
    equipment.value.push({
      id: item.value,
      title: item.title,
    });
  });
};

const updateType = val => {
  type.value = val;
};

const getSystemParams = () => {
  const systems = [];

  system.value.forEach(item => {
    systems.push(item.id);
  });

  return systems;
};

const getBuildingParams = () => {
  let buildings = [];

  buildings = building.value.map(item => {
    return item.id;
  });

  return buildings;
};

const addEvent = async () => {
  if (!facility.value.id) {
    return;
  }

  const event = {
    clientKey: `new-${nextNewEventKey++}`,
    name: "",
    description: "",
    periodType: {},
    checklistId: null,
    checklistRequired: false,
    period: [],
    startedAt: "",
    finishedAt: "",
    works: await getInitialWorks(),
    materials: [],
    materialsToRemove: [],
    materialsOptions: [],
    deadline: "",
    id: null,
  };
  events.value.push(event);
};

const removeEvent = index => {
  const eventToRemove = events.value[index];

  if (!eventToRemove) {
    return;
  }

  removedEvents.value.push({
    id: eventToRemove.id,
    clientKey: eventToRemove.clientKey,
    name: eventToRemove.name,
  });

  if (eventToRemove.id !== null && eventToRemove.id !== undefined && !eventsToRemove.value.includes(eventToRemove.id)) {
    eventsToRemove.value.push(eventToRemove.id);
  }

  events.value.splice(index, 1);
};

const getInitialWorks = async (query = "", page = 1, selected = [], prevOptions = [], { isCopy = false } = {}) => {
  const params = {
    facility_id: facility.value.id,
    scheduler: true,
    search_query: query,
    infinite_scroll: {
      page: page,
      per_page: 20,
    },
  };

  if (contract.value?.id) {
    params.contract_id = contract.value.id;
  }

  const works = {
    options: [...prevOptions],
    items: [],
    toRemove: [],
    hasNextPage: false,
    page: page,
  };

  try {
    const { data } = await backend.collection(`${store.state.paths["work"]}/collection`, params);

    data.options.forEach(option => {
      option["selected"] = false;
      works.options.push(option);
    });

    if (hasNextPage(works.page, data.count)) {
      works.hasNextPage = true;
      works.page++;
    }

    if (selected && selected.length > 0) {
      selected.forEach(item => {
        works.items.push({
          id: item.work.id,
          title: item.work.title,
          cost: item.work.cost,
          standard_hours: item.work.standard_hours,
          count: Number(item.count) - 1,
          selected: true,
        });

        if (!isCopy) {
          works.toRemove.push(item.id);
        }
      });

      works.options.map(option => {
        if (works.items.findIndex(work => work.id === option.id) !== -1) {
          option.selected = true;
        }

        return;
      });
    }
  } catch (err) {
    await handleError(err);
  }

  return works;
};

const reloadEventsWorks = async () => {
  if (!facility.value?.id || events.value.length === 0) {
    return;
  }

  for (const event of events.value) {
    event.works = await getInitialWorks();
  }
};

const hasNextPage = (page, count) => {
  const lastPage = Math.ceil(count / 20);
  return page < lastPage;
};

const searchWorks = async (event, query) => {
  await getWorks(event, query, true);
};

const getWorks = async (event, query, searching = false) => {
  const params = {
    facility_id: facility.value.id,
    scheduler: true,
    search_query: query,
    infinite_scroll: {
      page: searching ? 1 : event.works.page,
      per_page: 20,
    },
  };

  if (contract.value?.id) {
    params.contract_id = contract.value.id;
  }

  const data = await getWorksData(params);

  if (searching) {
    event.works.options = [];
  }

  data.options.forEach(option => {
    option["selected"] = false;
    event.works.options.push(option);
  });

  if (hasNextPage(event.works.page, data.count)) {
    event.works.hasNextPage = true;
    event.works.page++;
  } else {
    event.works.hasNextPage = false;
  }

  event.works.options.map(option => {
    if (event.works.items.findIndex(work => work.id === option.id) !== -1) {
      option.selected = true;
    }

    return;
  });
};

const getWorksData = async params => {
  let data;

  try {
    const resp = await backend.collection(`${store.state.paths["work"]}/collection`, params);
    data = resp.data;
  } catch (err) {
    await handleError(err);
  }

  return data;
};

const getMaterials = async id => {
  const params = {
    facility_id: id,
    scheduler: true,
  };
  const options = [];

  try {
    const { data } = await backend.collection(`${store.state.paths["material"]}/collection`, params);

    data.options.forEach(option => {
      option["selected"] = false;
      options.push(option);
    });
  } catch (err) {
    await handleError(err);
  }

  return options || [];
};

const closeEvent = mouseEvent => {
  const el = mouseEvent.currentTarget;
  const eventEl = el.closest(".form-step-four-event");
  eventEl.classList.toggle("close");
};

const closeWorks = mouseEvent => {
  const el = mouseEvent.currentTarget;
  const worksEl = el.closest(".form-step-four-event-works-left");
  worksEl.classList.toggle("close");
};

const closeMaterials = () => {
  const el = event.target;
  const worksEl = el.closest(".form-step-four-event-materials-left");
  worksEl.classList.toggle("close");
};

const selectWork = (event, option) => {
  const optionInEventIndex = event.works.items.findIndex(work => work.id === option.id);

  if (optionInEventIndex !== -1) {
    option["selected"] = false;
    option.count = 0;
    event.works.items.splice(optionInEventIndex, 1);

    if (event.works.options.find(item => item.id === option.id)) {
      event.works.options.find(item => item.id === option.id).selected = false;
    }
  } else {
    option["selected"] = true;
    event.works.items.push(option);
  }
};

const selectMaterial = (event, option) => {
  const optionInEventIndex = event.materials.findIndex(work => work.id === option.id);

  if (optionInEventIndex !== -1) {
    option["selected"] = false;
    option.count = 0;
    event.materials.splice(optionInEventIndex, 1);
    event.materialsOptions.find(item => item.id === option.id).selected = false;
  } else {
    option["selected"] = true;
    event.materials.push(option);
  }
};

const changeCount = (operator, option) => {
  if (operator === "-" && option.count - 1 == -1) {
    return;
  }

  operator === "+" ? option.count++ : option.count--;
};

const calculateCost = (count, cost) => {
  if (!cost) {
    return null;
  }

  return rounded(count * cost);
};

const getCalculateCost = (count, cost) => {
  return calculateCost(count, cost) ? calculateCost(count, cost) : null;
};

const calculateHours = (count, hours) => {
  if (!hours) {
    return 0;
  }

  return rounded(count * hours);
};

const getAllHours = event => {
  let summ = 0;

  event.works.items.forEach(work => {
    summ += calculateHours(work.count + 1, work.standard_hours);
  });

  return summ !== 0 ? rounded(summ) : null;
};

const getAllCosts = items => {
  let summ = 0;

  items.forEach(item => {
    summ += calculateCost(item.count + 1, item.cost);
  });

  return summ !== 0 ? `${rounded(summ)}₽` : null;
};

const getTotalCost = event => {
  let summ = 0;

  event.materials.forEach(material => {
    summ += calculateCost(material.count + 1, material.cost);
  });

  event.works.items.forEach(work => {
    summ += calculateCost(work.count + 1, work.cost);
  });

  return summ !== 0 ? `${rounded(summ)}₽` : null;
};

const updateChecklist = (event, val) => {
  event.checklistId = val.checklistId;
  event.checklistRequired = Boolean(val.checklistId && val.checklistRequired);
};

const updatePeriod = (val, event) => {
  event.periodType = {
    id: val.value,
    title: val.title,
  };
  setPeriodOptions(event);
};

const setPeriodOptions = event => {
  switch (event.periodType.id) {
    case "daily":
      event.period = [
        {
          id: "Monday",
          title: dateLocales.value.daysShort[1],
          selected: false,
        },
        {
          id: "Tuesday",
          title: dateLocales.value.daysShort[2],
          selected: false,
        },
        {
          id: "Wednesday",
          title: dateLocales.value.daysShort[3],
          selected: false,
        },
        {
          id: "Thursday",
          title: dateLocales.value.daysShort[4],
          selected: false,
        },
        {
          id: "Friday",
          title: dateLocales.value.daysShort[5],
          selected: false,
        },
        {
          id: "Saturday",
          title: dateLocales.value.daysShort[6],
          selected: false,
        },
        {
          id: "Sunday",
          title: dateLocales.value.daysShort[0],
          selected: false,
        },
      ];
      break;

    case "monthly_date":
      event.period = [];

      for (let i = 1; i <= 31; i++) {
        event.period.push({
          id: i,
          title: i,
          selected: false,
        });
      }

      break;

    case "weekly":
      event.period = [];

      for (let i = 0; i < 52; i++) {
        event.period.push({
          id: i + 1,
          title: i + 1,
          selected: false,
        });
      }

      break;

    case "monthly":
      event.period = [];

      for (let i = 1; i <= 12; i++) {
        const date = new Date(`2022-${i}-01`);
        event.period.push({
          id: date.toLocaleString("en", { month: "long" }),
          title: date.toLocaleString(currentLocale.value, { month: "long" }),
          selected: false,
        });
      }

      break;

    case "yearly":
      event.period = [];
      const date = new Date();
      let currentYear = date.getFullYear();
      const lastYear = currentYear + 17;

      while (currentYear + 1 < lastYear) {
        event.period.push({
          id: currentYear + 1,
          title: currentYear + 1,
          selected: false,
        });
        currentYear++;
      }

      break;

    default:
      event.period = [];
      break;
  }
};

const setPeriodOptionSelected = option => {
  option.selected ? (option.selected = false) : (option.selected = true);
};

const getSelectedEventPeriods = event => {
  return (event.period || []).filter(period => period.selected).map(period => period.id);
};

const selectStartDate = value => {
  value.event.startedAt = value.val;
};

const selectFinishDate = value => {
  value.event.finishedAt = value.val;
};

const saveForm = async () => {
  if (formSubmiting.value || disabledButton.value) {
    return;
  }

  const schedule = getParams();
  let res;

  try {
    disabledButton.value = true;

    if (props.method === "create") {
      res = await backend.create(path.value, "", { schedule }, { jsonRequest: true });
    } else {
      res = await backend.put(`${path.value}/${props.id}`, { schedule }, { jsonRequest: true });
    }

    formSubmiting.value = true;
    const data = {};
    data.data = res.data;
    data.method = props.method;
    closeForm(data);
  } catch (err) {
    await handleError(err);
  } finally {
    disabledButton.value = false;
  }
};

const submitForm = async () => {
  if (formSubmiting.value || disabledButton.value || !validForm()) {
    return;
  }

  if (removedEvents.value.length > 0) {
    deleteEventConfirmModalShown.value = true;
    return;
  }

  await saveForm();
};

const handleDeletedEventsConfirmation = async confirmed => {
  deleteEventConfirmModalShown.value = false;

  if (confirmed) {
    await saveForm();
  }
};

const getParams = () => {
  const params = {
    title: name.value,
    facility_id: facility.value.id,
    contract_id: contract.value?.id || null,
    group_system_ids: groupSystemIds.value,
    planning_by: type.value,
    schedule_placements_attributes: [],
    schedule_layouts_attributes: [],
    schedule_events_attributes: [],
  };

  if (building.value.length > 0 || buildingsToRemove.value.length > 0) {
    building.value.forEach(item => {
      params.schedule_placements_attributes.push({
        id: null,
        placement_type: "Building",
        placement_id: item.id,
      });
    });

    buildingsToRemove.value.forEach(id => {
      params.schedule_placements_attributes.unshift({
        _destroy: 1,
        id: id,
      });
    });
  }

  if (type.value !== "equipment") {
    equipment.value = [];
  }

  if (equipment.value.length > 0) {
    equipment.value.forEach(item => {
      params.schedule_layouts_attributes.push({
        id: null,
        layout_id: item.id,
        layout_type: "Ppr::Equipment",
      });
    });

    if (systemToRemove.value.length > 0) {
      systemToRemove.value.forEach(id => {
        params.schedule_layouts_attributes.unshift({
          id: id,
          _destroy: 1,
        });
      });
    }

    equipmentToRemove.value.forEach(id => {
      params.schedule_layouts_attributes.unshift({
        id: id,
        _destroy: 1,
      });
    });
  } else if (system.value.length > 0) {
    system.value.forEach(item => {
      params.schedule_layouts_attributes.push({
        id: null,
        layout_id: item.id,
        layout_type: "Ppr::System",
      });
    });

    if (equipmentToRemove.value.length > 0) {
      equipmentToRemove.value.forEach(id => {
        params.schedule_layouts_attributes.unshift({
          id: id,
          _destroy: 1,
        });
      });
    }

    systemToRemove.value.forEach(id => {
      params.schedule_layouts_attributes.unshift({
        id: id,
        _destroy: 1,
      });
    });
  }

  eventsToRemove.value.forEach(id => {
    params.schedule_events_attributes.push({
      id,
      _destroy: 1,
    });
  });

  events.value.forEach(event => {
    const eventWorks = [];
    const eventMaterials = [];

    event.materials.forEach(material => {
      eventMaterials.push({
        id: null,
        material_id: material.id,
        count: material.count + 1,
      });
    });

    event.materialsToRemove.forEach(id => {
      eventMaterials.unshift({
        _destroy: 1,
        id: id,
      });
    });

    event.works.items.forEach(work => {
      eventWorks.push({
        id: null,
        work_id: work.id,
        count: work.count + 1,
      });
    });

    event.works.toRemove.forEach(id => {
      eventWorks.unshift({
        _destroy: 1,
        id: id,
      });
    });

    params.schedule_events_attributes.push({
      title: event.name,
      description: event.description,
      period_type: event.periodType.id,
      checklist_id: event.checklistId,
      checklist_required: event.checklistRequired,
      period: getSelectedEventPeriods(event),
      event_works_attributes: eventWorks,
      event_materials_attributes: eventMaterials,
      started_at: event.startedAt,
      finished_at: event.finishedAt,
      days_to_overdue: event.deadline,
      id: event.id ? event.id : null,
    });
  });

  return params;
};

const validForm = () => {
  if (name.value.length <= 0) {
    Notify.create(pprScheduleLocales.value["form.validations.name"]);
    return false;
  }

  if (!facility.value.id) {
    Notify.create(pprScheduleLocales.value["form.validations.facility"]);
    return false;
  }

  if (system.value.length === 0) {
    Notify.create(pprScheduleLocales.value["form.validations.system"]);
    return false;
  }

  if (type.value === "equipment" && equipment.value.length === 0) {
    Notify.create(schedulerLocales.value["equipmentRequired"]);
    return false;
  }

  let eventErr = "";

  events.value.forEach(event => {
    if (!event.name) {
      if (events.value.length > 1) {
        eventErr = pprScheduleLocales.value["form.errors.events.name"];
      } else {
        eventErr = pprScheduleLocales.value["form.errors.event.name"];
      }

      return;
    }

    if (!event.periodType.id || !event.period.length > 0) {
      if (events.value.length > 1) {
        eventErr = pprScheduleLocales.value["form.errors.events.period"];
      } else {
        eventErr = pprScheduleLocales.value["form.errors.event.period"];
      }

      return;
    }

    if (!event.startedAt || !event.finishedAt) {
      eventErr = pprScheduleLocales.value["form.errors.event.started_at"];
      return;
    }

    // Use strict check to allow 0 as a valid deadline value
    const deadlineEmpty = event.deadline === null || event.deadline === undefined || event.deadline === "";

    if (deadlineEmpty) {
      eventErr = pprScheduleLocales.value["form.errors.event.deadline"];
      return;
    }

    if (Number(event.deadline) < 0) {
      eventErr = pprScheduleLocales.value["form.errors.event.deadline_negative"];
      return;
    }
  });

  if (eventErr !== "") {
    Notify.create(eventErr);
    return false;
  }

  return true;
};

const populateFormFromSchedule = async (data, { isCopy = false } = {}) => {
  name.value = data.title;
  facility.value = data.facility || {};
  groupSystems.value = data.group_systems ?? [];

  if (data.planning_by) {
    type.value = data.planning_by;
  }

  if (data.contract) {
    contract.value = {
      id: data.contract.id ?? data.contract_id,
      title: data.contract.title ?? "",
    };
  } else if (data.contract_id) {
    contract.value = {
      id: data.contract_id,
      title: data.contract_title ?? "",
    };
  }

  (data.schedule_placements || []).forEach(item => {
    building.value.push(item.placement);

    if (!isCopy && item.id) {
      buildingsToRemove.value.push(item.id);
    }
  });

  (data.schedule_layouts || []).forEach(item => {
    if (item.layout_type === "Ppr::Equipment") {
      type.value = "equipment";
      equipment.value.push({
        id: item.layout.id,
        title: item.layout.full_title,
      });

      if (!isCopy && item.id) {
        equipmentToRemove.value.push(item.id);
      }

      if (!system.value.some(systemItem => systemItem.id === item.layout.system.id)) {
        system.value.push({
          id: item.layout.system.id,
          title: item.layout.system.title,
        });
      }
    } else if (item.layout_type === "Ppr::System") {
      if (!isCopy && item.id) {
        systemToRemove.value.push(item.id);
      }

      system.value.push({
        id: item.layout.id,
        title: item.layout.title,
      });
    }
  });

  const scheduleEvents = data.schedule_events || [];

  for (let i = 0; i < scheduleEvents.length; i++) {
    const item = scheduleEvents[i];
    const event = {
      actions: (item.actions || []).map(action => ({
        ...action,
        clicked: false,
      })),
      name: item.title,
      description: item.description || "",
      periodType: periodTypeOptions.value.find(period => period.id === item.period_type) || {},
      period: [],
      startedAt: parsedDate(item.started_at).toLocaleDateString(),
      finishedAt: parsedDate(item.finished_at).toLocaleDateString(),
      works: await getInitialWorks("", 1, item.event_works, [], { isCopy }),
      materials: [],
      materialsToRemove: [],
      materialsOptions: await getMaterials(facility.value.id),
      deadline: item.days_to_overdue ?? "",
      id: isCopy ? null : item.id,
      clientKey: isCopy ? `new-${nextNewEventKey++}` : null,
      checklistId: item.checklist_id ?? item.checklist?.id ?? item.checklist?.value ?? null,
      checklistRequired: Boolean(item.checklist_required),
    };
    setPeriodOptions(event);

    (item.period || []).forEach(el => {
      if (el) {
        const periodEl = event.period.find(i => i.id.toString() === el.toString());

        if (periodEl) periodEl.selected = true;
      }
    });

    (item.event_materials || []).forEach(eventItem => {
      event.materials.push({
        id: eventItem.material.id,
        title: eventItem.material.title,
        count: Number(eventItem.count) - 1,
        cost: eventItem.material.cost,
        selected: true,
      });

      if (!isCopy && eventItem.id) {
        event.materialsToRemove.push(eventItem.id);
      }
    });

    event.materialsOptions.map(option => {
      if (event.materials.findIndex(material => material.id === option.id) !== -1) {
        option.selected = true;
      }

      return;
    });

    events.value.push(event);
  }
};

const getFormValues = async () => {
  try {
    if (props.method === "edit") {
      const { data } = await backend.show(path.value, props.id);
      await populateFormFromSchedule(data);
    } else if (props.copySourceId) {
      const { data } = await backend.index(`${path.value}/${props.copySourceId}/copy`);
      await populateFormFromSchedule(data, { isCopy: true });
    }
  } catch (err) {
    await handleError(err);
  } finally {
    loading.value = false;
  }
};

const updateNameField = val => {
  name.value = val;
};

const resetEventName = event => {
  event.name = "";
};

const rounded = number => {
  return number ? +number.toFixed(2) : null;
};

const parsedDate = date => {
  // "UTC" приходит с бека в конце текстового формата времени при отсутствии разницы в таймзоне.
  // Если разница есть, то она приходит в формате +hhmm без UTC
  const dateWithTimezone = date.replace("UTC", "+0000");
  return parse(dateWithTimezone, "yyyy-MM-dd HH:mm:ss xx", new Date());
};

provide("formFunctions", {
  closeEvent,
  resetEventName,
  updatePeriod,
  getAllHours,
  getAllCosts,
  addEvent,
  closeWorks,
  calculateHours,
  getCalculateCost,
  setPeriodOptionSelected,
  selectStartDate,
  selectFinishDate,
  changeCount,
  selectWork,
  getWorks,
  searchWorks,
  updateChecklist,
  getSelectedEventPeriods,
});

provide("formVariables", {
  facility,
  periodTypeOptions,
});

onBeforeMount(async () => {});

onMounted(async () => {
  await getFormValues();
  captureInitialFormSnapshot();
});

emitter.on(`request-form-close:${gridName}`, handleExternalCloseRequest);

onBeforeUnmount(() => {
  emitter.off(`request-form-close:${gridName}`, handleExternalCloseRequest);
});
</script>

<style lang="scss"></style>
